// Auth API (WP0, architecture §7.5 foundation table, §15).
import { z } from 'zod';
import type { StepUpPurpose } from '../domain.ts';

/** JSON of WebAuthn creation/request options as produced by @simplewebauthn/server (opaque to shared). */
export type WebAuthnOptionsJSON = Record<string, unknown>;

/** GET /api/auth/state */
export interface AuthState {
  origin: 'ok' | 'fehlt';
  hasPasskey: boolean;
  setupCodeConfigured: boolean;
  recovery: boolean;
  addDeviceOpen: boolean;
  loggedIn: boolean;
  /** WP0 addition: why the setup code cannot be used ('fehlt' = SETUP_CODE not set, 'zu_kurz' = shorter than 12). */
  setupCodeProblem: 'fehlt' | 'zu_kurz' | null;
}

export const VerifyCodeBody = z.object({ code: z.string().min(1).max(200) });
export type VerifyCodeBody = z.infer<typeof VerifyCodeBody>;
export interface VerifyCodeResponse { setupToken: string }

const Label = z.string().trim().min(1).max(60);
/** WebAuthn response JSON from @simplewebauthn/browser; checked in depth by @simplewebauthn/server. */
const CredentialJSON = z.object({ id: z.string().min(1).max(1024), rawId: z.string().min(1).max(1024), type: z.literal('public-key'), response: z.record(z.string(), z.unknown()) }).loose();

export const RegisterOptionsBody = z.object({ setupToken: z.string().max(200).optional(), label: Label });
export type RegisterOptionsBody = z.infer<typeof RegisterOptionsBody>;
export const RegisterVerifyBody = z.object({ response: CredentialJSON, setupToken: z.string().max(200).optional(), label: Label });
export type RegisterVerifyBody = z.infer<typeof RegisterVerifyBody>;
export const LoginVerifyBody = z.object({ response: CredentialJSON });
export type LoginVerifyBody = z.infer<typeof LoginVerifyBody>;

/** zod mirror of the domain type `StepUpPurpose` (§7.2, §15.5); `satisfies` keeps the list complete and exact. */
const STEP_UP_PURPOSES = { freigabe: 1, geraet: 1, passkey: 1, schluessel: 1, export: 1, import: 1, konfiguration: 1 } satisfies Record<StepUpPurpose, 1>;
export const StepUpPurposeSchema = z.enum(Object.keys(STEP_UP_PURPOSES) as [StepUpPurpose, ...StepUpPurpose[]]);
export const StepUpOptionsBody = z.object({ approvalId: z.uuid().optional(), purpose: StepUpPurposeSchema });
export type StepUpOptionsBody = z.infer<typeof StepUpOptionsBody>;
export const StepUpVerifyBody = z.object({ response: CredentialJSON });
export type StepUpVerifyBody = z.infer<typeof StepUpVerifyBody>;
export interface StepUpVerifyResponse { until: string }

/** POST /api/auth/add-device. `code` (WP0 addition): one-time 8-character code for registering on another device. */
export interface AddDeviceResponse { until: string; code: string }

export interface SessionView { id: string; label: string | null; userAgent: string | null; createdAt: string; lastSeenAt: string; current: boolean }
export interface PasskeyView { id: string; label: string | null; deviceType: 'singleDevice' | 'multiDevice'; backedUp: boolean; createdAt: string; lastUsedAt: string | null }
export const PatchPasskeyBody = z.object({ label: Label });
export type PatchPasskeyBody = z.infer<typeof PatchPasskeyBody>;

/** WP0 additions for Einstellungen → Sicherheit (§16): where the token key lives, and the one-time reveal. */
export interface TokenKeyState { source: 'env' | 'db' }
export interface TokenKeyReveal { key: string }
